import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { Settings, Trash2, Loader2, Save, Crown } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

export const ProfilesModal = ({ isOpen, onClose, currentAllergens, openWithForm = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showSaveForm, setShowSaveForm] = useState(openWithForm);
  const [profileName, setProfileName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadProfiles();
      if (openWithForm) {
        setShowSaveForm(true);
      }
    }
  }, [isOpen, openWithForm]);

  const loadProfiles = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/profiles/list');
      setProfiles(response.data.profiles || []);
    } catch (error) {
      console.error('Error loading profiles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveProfile = async () => {
    if (!profileName.trim()) {
      toast.error(t('profileNameRequired') || 'Nombre requerido');
      return;
    }

    setIsSaving(true);
    try {
      // currentAllergens already contains only selected allergens
      // Make sure each one has selected: true
      const allergensToSave = (currentAllergens || []).map(a => ({
        ...a,
        selected: true
      }));
      
      console.log('Saving profile with allergens:', allergensToSave);
      
      await api.post('/profiles/create', {
        profile_name: profileName,
        allergens: allergensToSave
      });
      toast.success(t('profileSaved') || 'Perfil guardado');
      setProfileName('');
      setShowSaveForm(false);
      loadProfiles();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Error al guardar el perfil');
    } finally {
      setIsSaving(false);
    }
  };

  const loadProfile = async (profileId) => {
    try {
      console.log('Loading profile:', profileId);
      const response = await api.post(`/profiles/load/${profileId}`);
      console.log('Load response:', response.data);
      toast.success(t('profileLoaded') || 'Perfil cargado');
      onClose();
      window.location.reload();
    } catch (error) {
      console.error('Error loading profile:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Error al cargar el perfil: ' + (error.response?.data?.detail || error.message));
    }
  };

  const deleteProfile = async (profileId) => {
    try {
      console.log('Deleting profile:', profileId);
      const response = await api.delete(`/profiles/${profileId}`);
      console.log('Delete response:', response.data);
      toast.success(t('profileDeleted'));
      setProfiles(profiles.filter(p => p.id !== profileId));
    } catch (error) {
      console.error('Error deleting profile:', error);
      console.error('Profile ID was:', profileId);
      console.error('Error response:', error.response?.data);
      toast.error('Error al eliminar el perfil');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-6 h-6 text-[#8FBC8F]" />
            {t('allergenProfiles')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Save current config as profile */}
          {!showSaveForm ? (
            <Button
              onClick={() => setShowSaveForm(true)}
              className="w-full bg-[#8FBC8F] hover:bg-[#7CAA7C]"
              data-testid="show-save-profile-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {t('saveAsProfile')}
            </Button>
          ) : (
            <Card>
              <CardContent className="p-4 space-y-3">
                <Label htmlFor="profile-name">{t('profileName')}</Label>
                <Input
                  id="profile-name"
                  data-testid="profile-name-input"
                  placeholder={t('profileNamePlaceholder')}
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                />
                <div className="flex gap-2">
                  <Button
                    onClick={saveProfile}
                    disabled={isSaving}
                    className="flex-1 bg-[#8FBC8F] hover:bg-[#7CAA7C]"
                    data-testid="save-profile-btn"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('saveProfile')}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowSaveForm(false);
                      setProfileName('');
                    }}
                    variant="outline"
                  >
                    {t('close')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* List of saved profiles */}
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#8FBC8F]" />
            </div>
          ) : profiles.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t('noProfilesYet')}
            </div>
          ) : (
            <div className="space-y-3">
              {profiles.map((profile) => {
                const selectedCount = profile.allergens.filter(a => a.selected).length;
                return (
                  <Card key={profile.id}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">{profile.profile_name}</h3>
                        <p className="text-sm text-gray-500">
                          {selectedCount} {selectedCount === 1 ? 'alérgeno' : 'alérgenos'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => loadProfile(profile.id)}
                          size="sm"
                          className="bg-[#8FBC8F] hover:bg-[#7CAA7C]"
                          data-testid={`load-profile-${profile.id}`}
                        >
                          {t('loadProfile')}
                        </Button>
                        <Button
                          onClick={() => deleteProfile(profile.id)}
                          size="sm"
                          variant="destructive"
                          data-testid={`delete-profile-${profile.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
          
          {/* Unlock unlimited profiles button */}
          <div className="mt-6 pt-4 border-t text-center">
            <Button
              onClick={() => {
                onClose();
                navigate('/pricing');
              }}
              variant="outline"
              className="border-2 border-yellow-500 text-yellow-600 hover:bg-yellow-50"
            >
              <Crown className="w-4 h-4 mr-2" />
              {t('unlockUnlimitedProfiles') || 'Añadir perfiles ilimitados'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};